import { type envType } from '@atlassiansox/analytics-web-client';

import { ProductIds } from '../../constants';
import type {
	Attributes,
	GlobalScriptEntry,
	PendoConfig,
	PendoEvent,
	PendoInstance,
} from '../../types';
import { ChoreographerPlugin } from '../ChoreographerPlugin';

/*
 * This plugin is designed to work with the Pendo agent, which is a third-party tool that is loaded into
 * the application by the products and is stored in window.pendo. This plugin essentially creates a proxy
 * object for the Pendo instance, which allows us to intercept and decorate certain functions within the
 * pendo API as they are set.
 */
class PendoChoreographerPlugin extends ChoreographerPlugin {
	private currentMessageId: string = '';

	// Symbol to store the original Pendo instance since window.pendo will be overwritten with our proxy
	private pendoSymbol = Symbol.for('choreographer-pendo-instance');

	constructor(envType: envType, metaMetricsData: Attributes = {}) {
		super(ProductIds.PENDO, envType, metaMetricsData);

		// If window.pendo is already defined, store it in our custom symbol reference
		(window as any)[this.pendoSymbol] = window.pendo ?? {};

		// Define a handler for the proxy to intercept property assignments
		const handler: ProxyHandler<PendoInstance> = {
			set: (target, prop, value, receiver) => {
				// Assign the original property directly to our symbol reference for restoration after teardown
				(window as any)[this.pendoSymbol][prop] = value;

				// Check if the property being set is one of the specific functions we want to decorate within pendo
				if (prop === 'initialize' || prop === 'updateOptions') {
					return Reflect.set(
						target,
						prop,
						(pendoConfig: PendoConfig) => {
							return value.call(receiver, this.getPendoConfig(pendoConfig));
						},
						receiver,
					);
				} else if (prop === 'teardown') {
					return Reflect.set(
						target,
						prop,
						() => {
							/* Call the target function as intended and store the return value. This is necessary
							 * because if we call this.destroy() before the teardown function, we get this undefined
							 * reference error when run in Jest:
							 *
							 * [TypeError: Cannot set properties of undefined (setting 'guidesProcessingThreadHandle')]
							 */

							const ret = value.call(receiver);

							// Remove the proxy and destroy this plugin instance
							this.destroy();

							return ret;
						},
						receiver,
					);
				}
				// For all other properties, assign them directly
				return Reflect.set(target, prop, value, receiver);
			},
		};

		// Initialize the Pendo API object as a Proxy
		const pendoProxy = new Proxy<PendoInstance>({}, handler);

		// Store the Proxy object as window.pendo and wait for pendo to initialize
		Object.defineProperty(window, 'pendo', {
			configurable: true,
			writable: true,
			value: pendoProxy,
		});

		// If we had a previous instance of pendo already defined, which was stored in this symbol reference,
		// duplicate its members into our Proxy object and let it determine whether to take them as-is or
		// decorate them for the plugin.
		if ((window as any)[this.pendoSymbol]) {
			Object.getOwnPropertyNames((window as any)[this.pendoSymbol]).forEach((key) => {
				window.pendo[key] = (window as any)[this.pendoSymbol][key];
			});
			Object.getOwnPropertySymbols((window as any)[this.pendoSymbol]).forEach((key) => {
				window.pendo[key] = (window as any)[this.pendoSymbol][key];
			});
		}
	}

	public getPendoConfig(pendoConfig: PendoConfig) {
		// eslint-disable-next-line @typescript-eslint/no-this-alias
		const pendoPlugin = this;

		return {
			...pendoConfig,
			guides: {
				...(pendoConfig.guides ?? {}),
				globalScripts: [
					...(pendoConfig.guides?.globalScripts ?? []),
					{
						beforeMount(evnt: PendoEvent) {
							if (pendoPlugin.state.isDisabled) {
								evnt.cancel = true;
							} else {
								pendoPlugin.currentMessageId = evnt.step.id;
								pendoPlugin.on(pendoPlugin.currentMessageId, {});
								void pendoPlugin.startMessage(pendoPlugin.currentMessageId);
							}
						},
						unmounted() {
							void pendoPlugin.stopMessage(pendoPlugin.currentMessageId);
						},
					} as GlobalScriptEntry,
				],
			},
		};
	}

	override isDisplayingMessage() {
		return Boolean(window.pendo.getActiveGuide?.());
	}

	public override destroy() {
		// Reassign the original unchoreographed Pendo instance back to window.pendo
		Object.defineProperty(window, 'pendo', {
			configurable: true,
			writable: true,
			value: (window as any)[this.pendoSymbol],
		});

		// Delete the symbol reference altogether
		delete (window as any)[this.pendoSymbol];

		super.destroy();
	}
}

export function registerPendo(envType: envType, metaMetricsData: Attributes = {}) {
	new PendoChoreographerPlugin(envType, metaMetricsData);
}
